import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type { CustomResponseType } from '@/types/CustomAxiosReponseType';
import type { DataTableOptionsType } from '@/types/DataTableOptionsType';
import { buildQueryString } from '@/utils/queryUtils';
import type { BaseCrudActionsType, BaseCrudStateType } from '@/types/stores/BaseStoreTypes';
import type { CrudStoreConfigType } from '@/types/CrudStoreConfigType';
import type { FilterChipType } from '@/types/statistics/FilterChipType';
import type { DateRangeType } from '@/types/statistics/DateRangeType';
import type { GroupingPeriodType } from '@/types/GroupingPeriodType';
import type { FilterFieldMetadata } from '@/types/FilterMetadataTypes';
import moment from 'moment';
import i18n from '@/plugins/i18n';
import type { VDataTableServer } from 'vuetify/components';
import type { SingleHeaderType } from '@/types/SingleHeaderType';
import { transformSortBy } from '@/utils/sortUtils';
import { usePageActionsStore } from '@/stores/pageActionsStore';

type CrudStoreContext<T> = BaseCrudStateType<T> & BaseCrudActionsType;

export function createCrudState<T>(config: CrudStoreConfigType<T>) {
    return () => ({
        items: [],
        editedItem: config.createEmptyItem(),
        isLoading: false,
        totalItems: 0,
        currentPage: 1,
        itemsPerPage: 25,
        endpoint: typeof config.endpoint === 'function' ? '' : config.endpoint,
        headers: config.headers,
        sortBy: config.sortBy,
        filterFields: [],
    });
}

export function createCrudGetters<T extends { id: number | string }>() {
    return {
        getTotalItems: (state: BaseCrudStateType<T>) => state.totalItems,
        getItemsPerPage: (state: BaseCrudStateType<T>) => state.itemsPerPage,
        getCurrentPage: (state: BaseCrudStateType<T>) => state.currentPage,
        getEditedItem: (state: BaseCrudStateType<T>) => state.editedItem,
        hasItems: (state: BaseCrudStateType<T>) => state.items.length > 0,
        getItemById: (state: BaseCrudStateType<T>) => (id: number | string) => state.items.find(item => item.id === id),
        getFilterFields(state: BaseCrudStateType<T>): FilterFieldMetadata[] {
            return state.filterFields;
        },
        getHeaders:
            (state: BaseCrudStateType<T>) =>
                (actionsConfig: boolean | SingleHeaderType | undefined = true): VDataTableServer['$props']['headers'] => {
                    const headers =
                    state.headers?.map(header => ({
                        ...header,
                        title: header.title ? i18n.global.t(header.title) : header.title,
                    })) || [];

                    if (actionsConfig !== false) {
                        const defaultActionsConfig: SingleHeaderType = {
                            sortable: false,
                            title: '',
                        };

                        const finalActionsConfig =
                        actionsConfig === true ? defaultActionsConfig : { ...defaultActionsConfig, ...actionsConfig };

                        const actionsColumn: SingleHeaderType = {
                            key: 'actions',
                            title: finalActionsConfig.title || '',
                            sortable: finalActionsConfig.sortable ?? false,
                            align: 'end',
                        };

                        return [...headers, actionsColumn];
                    }

                    return headers;
                },
    };
}

export function createCrudActions<T extends { id: number | string }>(config: CrudStoreConfigType<T>) {
    const getEndpoint = (context: CrudStoreContext<T>): string => {
        if (typeof config.endpoint === 'function') {
            return config.endpoint(context);
        }

        return config.endpoint;
    };

    return {
        async fetchAll(this: CrudStoreContext<T>, options: Partial<DataTableOptionsType>) {
            const pageStore = usePageActionsStore();
            pageStore.setLoading(true);

            this.isLoading = true;
            const query = buildQueryString(options);
            const endpoint = getEndpoint(this);
            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: `${endpoint}?${query}`,
                onSuccess: (response: CustomResponseType<T[]>) => {
                    this.items = response.data.items;
                    this.totalItems = response.data.total;
                    this.currentPage = options.page || 1;

                    if (response.data.filters) {
                        this.filterFields = response.data.filters.fields || [];
                    }
                },
            });

            this.isLoading = false;

            pageStore.setLoading(false);
        },

        async fetchOne(this: CrudStoreContext<T>, id: number | string): Promise<boolean> {
            this.isLoading = true;
            let result = false;
            const endpoint = getEndpoint(this);

            if (config.hasShowEndpoint === false) {
                let item = this.items.find(item => item.id === id);

                if (!item && this.items.length === 0) {
                    await this.fetchAll({ showAllItems: true });
                    item = this.items.find(item => item.id === id);
                }

                if (item) {
                    this.editedItem = JSON.parse(JSON.stringify(item));
                    result = true;
                }
            } else {
                await handleRequest({
                    method: RequestMethodEnum.Get,
                    endpoint: `${endpoint}/${id}`,
                    onSuccess: (response: CustomResponseType<T>) => {
                        this.editedItem = response.data.data;
                        result = true;
                    },
                });
            }

            this.isLoading = false;

            return result;
        },

        async createItem(this: CrudStoreContext<T>, customData?: Partial<T>): Promise<boolean> {
            this.isLoading = true;
            let result: boolean = false;
            const endpoint = getEndpoint(this);

            let dataToSubmit = customData || this.editedItem;
            if (config.transformForCreate) {
                dataToSubmit = config.transformForCreate(dataToSubmit as T);
            }
            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: `${endpoint}`,
                data: dataToSubmit,
                successMessageKey: config.successMessages.created,
                onSuccess: (response: CustomResponseType<T>) => {
                    this.editedItem = response.data.data;
                    this.items.push(this.editedItem);
                    this.totalItems += 1;
                    result = true;
                },
                onError: () => {
                    result = false;
                },
            });
            this.isLoading = false;

            return result;
        },

        async updateItem(this: CrudStoreContext<T>): Promise<boolean> {
            this.isLoading = true;
            let result: boolean = false;
            const endpoint = getEndpoint(this);
            await handleRequest({
                method: RequestMethodEnum.Put,
                endpoint: `${endpoint}/${this.editedItem.id}`,
                data: this.editedItem,
                successMessageKey: config.successMessages.updated,
                onSuccess: (response: CustomResponseType<T>) => {
                    this.editedItem = response.data.data;
                    this.items = updateItemInArray(this.items, this.editedItem);
                    result = true;
                },
                onError: () => {
                    result = false;
                },
            });
            this.isLoading = false;

            return result;
        },

        async deleteItem(this: CrudStoreContext<T>, id: number | string): Promise<boolean> {
            this.isLoading = true;
            let result: boolean = false;
            const endpoint = getEndpoint(this);
            await handleRequest({
                method: RequestMethodEnum.Delete,
                endpoint: `${endpoint}/${id}`,
                successMessageKey: config.successMessages.deleted,
                onSuccess: async () => {
                    this.items = removeItemFromArray(this.items, id);
                    this.totalItems -= 1;

                    // Check if current page is empty after deletion
                    const totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
                    if (this.currentPage > totalPages && totalPages > 0) {
                        // Move to the last available page
                        this.currentPage = totalPages;
                    }

                    await this.fetchAll({
                        page: this.currentPage,
                        itemsPerPage: this.itemsPerPage,
                        ...transformSortBy(this.sortBy),
                    });

                    result = true;
                },
                onError: () => {
                    result = false;
                },
            });
            this.isLoading = false;

            return result;
        },

        resetEditedItem(this: CrudStoreContext<T>): void {
            this.editedItem = config.createEmptyItem();
        },

        prepareItemForDuplication(this: CrudStoreContext<T>, item: T): void {
            const { id: _id, ...itemWithoutId } = item;
            const itemCopy = { ...itemWithoutId } as T;

            if (config.duplicationConfig) {
                const { fieldsToDelete, fieldsToCopy } = config.duplicationConfig;

                if (fieldsToDelete) {
                    for (const field of fieldsToDelete) {
                        delete itemCopy[field];
                    }
                }

                if (fieldsToCopy) {
                    for (const field of fieldsToCopy) {
                        const value = itemCopy[field];

                        if (typeof value === 'string') {
                            itemCopy[field] = `${value} (${i18n.global.t('common.copy')})` as T[Extract<
                                keyof T,
                                string
                            >];
                        }
                    }
                }
            }

            this.editedItem = itemCopy;
        },
    };
}

export function updateItemInArray<T extends { id: number | string }>(items: T[], updatedItem: T): T[] {
    const index = items.findIndex(item => item.id === updatedItem.id);
    if (index !== -1) {
        const newItems = [...items];
        newItems[index] = { ...updatedItem };

        return newItems;
    }

    return items;
}

export function removeItemFromArray<T extends { id: number | string }>(items: T[], id: number | string): T[] {
    return items.filter(item => item.id !== id);
}

export function formatDateRangeChip(dateRange: DateRangeType): FilterChipType {
    if (!dateRange || !dateRange.start || !dateRange.end) {
        throw new Error('Invalid date range provided to formatDateRangeChip');
    }

    const startDate = moment(dateRange.start);
    const endDate = moment(dateRange.end);

    const label = startDate.isSame(endDate, 'day')
        ? startDate.format('MMM D, YYYY')
        : `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;

    return {
        key: 'dateRange',
        label,
        value: dateRange,
        removable: true,
        icon: 'mdi-calendar-range',
    };
}

type AntennaDataType = {
    antenna_uuid: string;
    antenna_name: string;
    location_id?: number | string | null;
    location_name?: string | null;
};

type GroupDataType = {
    data: AntennaDataType[];
};

export function createAntennaFilterChip(
    antennaUuids: string[] | null | undefined,
    groups: GroupDataType[],
): FilterChipType | null {
    if (!antennaUuids || antennaUuids.length === 0) {
        return null;
    }

    const { t } = i18n.global;
    const antennaNames: string[] = [];

    for (const uuid of antennaUuids) {
        for (const group of groups) {
            for (const antenna of group.data) {
                if (antenna.antenna_uuid === uuid && !antennaNames.includes(antenna.antenna_name)) {
                    antennaNames.push(antenna.antenna_name);
                    break;
                }
            }
        }
    }

    const label =
        antennaNames.length === 1
            ? `${t('statistics.filters.chips.antenna')}: ${antennaNames[0]}`
            : `${t('statistics.filters.chips.antenna')}: ${antennaNames.length} selected`;

    return {
        key: 'antennaUuid',
        label,
        value: antennaUuids,
        removable: true,
        icon: 'mdi-antenna',
    };
}

export function createLocationFilterChip(
    locationIds: (number | string)[] | null | undefined,
    groups: GroupDataType[],
): FilterChipType | null {
    if (!locationIds || locationIds.length === 0) {
        return null;
    }

    const { t } = i18n.global;
    const locationNames: string[] = [];

    for (const locationId of locationIds) {
        for (const group of groups) {
            for (const antenna of group.data) {
                if (
                    antenna.location_id === locationId &&
                    antenna.location_name &&
                    !locationNames.includes(antenna.location_name)
                ) {
                    locationNames.push(antenna.location_name);
                    break;
                }
            }
        }
    }

    const label =
        locationNames.length === 1
            ? `${t('statistics.filters.chips.location')}: ${locationNames[0]}`
            : `${t('statistics.filters.chips.location')}: ${locationNames.length} selected`;

    return {
        key: 'locationId',
        label,
        value: locationIds,
        removable: true,
        icon: 'mdi-map-marker',
    };
}

type EndpointGroupDataType = {
    data: { endpoint: string }[];
};

export function createEndpointFilterChip(
    endpoints: string[] | null | undefined,
    groups: EndpointGroupDataType[],
): FilterChipType | null {
    if (!endpoints || endpoints.length === 0) {
        return null;
    }

    const { t } = i18n.global;
    const endpointNames: string[] = [];

    for (const endpoint of endpoints) {
        for (const group of groups) {
            for (const item of group.data) {
                if (item.endpoint === endpoint && !endpointNames.includes(endpoint)) {
                    endpointNames.push(endpoint);
                    break;
                }
            }
        }
    }

    for (const endpoint of endpoints) {
        if (!endpointNames.includes(endpoint)) {
            endpointNames.push(endpoint);
        }
    }

    const label =
        endpointNames.length === 1
            ? `${t('statistics.filters.chips.endpoint')}: ${endpointNames[0]}`
            : `${t('statistics.filters.chips.endpoint')}: ${endpointNames.length} selected`;

    return {
        key: 'endpoints',
        label,
        value: endpoints,
        removable: true,
        icon: 'mdi-api',
    };
}

// Shared utility for building statistics query parameters
export function buildStatisticsQueryParams(filters: {
    dateRange?: DateRangeType | null;
    groupedBy: GroupingPeriodType;
    [key: string]: unknown;
}): Record<string, string | number | string[] | number[]> {
    const queryParams: Record<string, string | number | string[] | number[]> = {
        startDate: filters.dateRange?.start
            ? moment(filters.dateRange.start).format('YYYY-MM-DD')
            : moment().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: filters.dateRange?.end
            ? moment(filters.dateRange.end).format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD'),
        groupedBy: filters.groupedBy,
    };

    return queryParams;
}

/**
 * Applies autocomplete prefix to field name if needed.
 * If fieldName already contains a dot, assumes it's already prefixed.
 *
 * @param fieldName - The field name to prefix (e.g., "name", "type")
 * @param prefix - Optional prefix (e.g., "locations", "promotions")
 * @returns Prefixed field name in "table.field" format (e.g., "locations.name")
 *
 * @example
 * applyAutocompletePrefix('name', 'locations') // Returns: 'locations.name'
 * applyAutocompletePrefix('locations.name', 'locations') // Returns: 'locations.name' (no double-prefix)
 * applyAutocompletePrefix('name', undefined) // Returns: 'name' (no prefix configured)
 */
export function applyAutocompletePrefix(fieldName: string, prefix?: string): string {
    // If no prefix configured or no fieldName, return as-is
    if (!prefix || !fieldName) {
        return fieldName;
    }

    // If fieldName already contains a dot, assume it's already prefixed
    if (fieldName.includes('.')) {
        return fieldName;
    }

    // Apply prefix in table.field format
    return `${prefix}.${fieldName}`;
}

/**
 * Creates a fetchValueSuggestions action for CRUD stores.
 * This utility eliminates the need to duplicate autocomplete logic across stores.
 *
 * @param config - The CRUD store configuration
 * @returns An async action that fetches autocomplete suggestions
 *
 * @example
 * const config: CrudStoreConfigType<LocationType> = {
 *   autocompletePrefix: 'locations',
 *   autocompleteConfig: {
 *     endpoint: '/autocomplete',
 *     responseTransformer: (response) => response.data.data
 *   }
 * };
 *
 * actions: {
 *   fetchValueSuggestions: createFetchValueSuggestionsAction(config)
 * }
 */
export function createFetchValueSuggestionsAction<T>(config: CrudStoreConfigType<T>) {
    return async function (
        this: BaseCrudStateType<T> & {
            valueSuggestions: string[];
            isSuggestionsLoading: boolean;
        },
        fieldName: string,
        autocomplete?: string,
        query = '',
    ): Promise<string[]> {
        this.valueSuggestions = [];
        this.isSuggestionsLoading = true;

        // Use autocomplete value from filter field if provided, otherwise fallback to prefix
        const autocompleteName = autocomplete || applyAutocompletePrefix(fieldName, config.autocompletePrefix);

        const queryString = buildQueryString({
            name: autocompleteName,
            query,
        });

        const endpoint = config.autocompleteConfig?.endpoint || '/autocomplete';

        let suggestions: string[] = [];

        await handleRequest({
            method: RequestMethodEnum.Get,
            endpoint: `${endpoint}?${queryString}`,
            canShowSnackbar: false,
            onSuccess: (response: CustomResponseType<any>) => {
                suggestions = response.data.data;
                this.valueSuggestions = suggestions;
            },
            onError: () => {
                suggestions = [];
                this.valueSuggestions = [];
            },
        });

        this.isSuggestionsLoading = false;

        return suggestions;
    };
}
